import { Navigate, Outlet, useParams, useLocation } from "react-router-dom";
import Loader from "../components/Loader";
import { useEffect } from "react";
import { useGetMeQuery } from "../services/api";
import { useAppDispatch, useAppSelector } from "../store/store";
import { setStage } from "../store/reducers/stageReducer";
import { setPurlUser } from "../store/reducers/purlUserReducer";
import {
  setUserFolderId,
  setUserUniqueId,
} from "../store/reducers/usersReducer";
import { setPermission } from "../store/reducers/permissionReducer";
import { setUserFlow } from "../store/reducers/userFlowReducer";
import { Roles } from "../utils/role";
import { MASTER_TOKEN_KEY } from "../services/masterApi";
import { isImpersonating } from "./impersonation";
import { ONBOARDING_STEPS } from "../pages/Onboarding/steps";
import useHasPermission from "../hooks/useHasPermission";

const PurlAuthHandler = ({ isProtected }: { isProtected: boolean }) => {
  const { id } = useParams<{ id?: string }>();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const token = localStorage.getItem("token");
  const stage = useAppSelector((state) => state.stage);
  const { hasPermission } = useHasPermission();

  const isMasterUser =
    !!localStorage.getItem(MASTER_TOKEN_KEY) &&
    localStorage.getItem("userType") === "MASTER_USER";

  const {
    data: meData,
    isLoading,
    isError,
    isUninitialized,
  } = useGetMeQuery(undefined, {
    skip: !token,
  });

  useEffect(() => {
    if (!meData?.data) return;

    const { user, purlStage, driveData } = meData.data;

    dispatch(
      setStage({
        ...purlStage,
        billOfSaleSignedDate: purlStage.billOfSaleSignedDate
          ? new Date(purlStage.billOfSaleSignedDate)
          : null,
      })
    );

    dispatch(
      setPurlUser({
        uniqueId: user.uniqueId,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.mobile,
        creatorId: user.creatorId,
        rowId: user.rowId,
        solarStudyInvite: user.solarStudyInvite,
        contactGroup: user.contactGroupType,
        contactOwner: user.contactOwner,
        contactOwnerEmail: user.contactOwnerEmail,
        advisorName: user.advisorName,
        advisorEmail: user.advisorEmail,
        cpaName: user.cpaName,
        cpaEmail: user.cpaEmail,
        bookkeeperName: user.bookkeeperName,
        bookkeeperEmail: user.bookkeeperEmail,
        entityName: user.entity?.name ?? null,
        crmContactId: user.zohoCustomerId,
        annualIncome: user.annualIncome ?? null,
        stateOfResidence: user.stateOfResidence ?? null,
        // TODO - VERIFY EVERY FIELD IS ADDED
      })
    );

    dispatch(setPermission({ contactGroup: user.contactGroupType }));

    const folderId = driveData?.purlDriveLink ?? driveData?.GDriveID;
    if (folderId) dispatch(setUserFolderId(folderId));
    if (user.uniqueId) dispatch(setUserUniqueId(user.uniqueId));
    dispatch(
      setUserFlow({
        contactGroup: user.contactGroupType,
        role: meData.data.role || Roles.CUSTOMER,
        rolePermissions: {
          [meData.data.role || Roles.CUSTOMER]: meData.data.permissions,
        },
      })
    );
    // TODO - CHECK IF WE NEED TO HYDRATE ANY OTHER STATE HERE
    // const step = setStepFunc(purlStage);
  }, [meData]);

  if (token && (isLoading || isUninitialized)) {
    return (
      <div className="h-dvh">
        <Loader isLoading={true} />
      </div>
    );
  }

  if (token && isError) {
    localStorage.removeItem("token");
  }

  if (isMasterUser && !isImpersonating()) {
    return <Navigate to="/users" replace />;
  }

  const isAuthenticated = !!token && !isError && !!meData?.data;
  const uniqueId = meData?.data?.user?.uniqueId || id;

  if (isProtected) {
    if (isAuthenticated) {
      // Master user tried to access another client with uniqueId while impersonating some other.
      if (isMasterUser && isImpersonating()) {
        const sessionUser = meData?.data?.user;
        const matchesSession =
          !!id &&
          (id === sessionUser?.uniqueId || id === sessionUser?.zohoCustomerId);
        if (!matchesSession) {
          return <Navigate to="/users" replace />;
        }
      } else {
        // Regular user: data is always driven by the token, so a hand-edited ,uniqueId from me
        const genuineId = meData?.data?.user?.uniqueId;
        if (genuineId && id && id !== genuineId) {
          const correctedPath = location.pathname
            .split("/")
            .map((segment) => (segment == id ? genuineId : segment))
            .join("/");
          return (
            <Navigate
              to={`${correctedPath}${location.search}${location.hash}`}
              replace
            />
          );
        }
      }

      // Guard: prevent dashboard access if onboarding is incomplete
      const isDashboardRoute = location.pathname.startsWith("/dashboard");
      const genuineUniqueId = meData?.data?.user?.uniqueId;
      const userRole = meData?.data?.role || Roles.CUSTOMER;

      if (
        isDashboardRoute &&
        genuineUniqueId &&
        !isMasterUser &&
        userRole === Roles.CUSTOMER
      ) {
        const isOnboardingComplete = ONBOARDING_STEPS.every((step) => {
          const isPermitted = !step.permissionKey || hasPermission(step.permissionKey);
          if (!isPermitted) return true; // skip non-permitted steps (same as Onboarding page)
          return !!stage[step.key as keyof typeof stage];
        });

        if (!isOnboardingComplete) {
          return <Navigate to={`/onboarding/${genuineUniqueId}`} replace />;
        }
      }

      return <Outlet />;
    }

    if (!uniqueId) {
      return <Navigate to="/" replace />;
    }
    return <Navigate to={`/${uniqueId}`} replace />;
  }

  if (isAuthenticated) {
    if (!uniqueId) {
      return <Navigate to="/" replace />;
    }
    return <Navigate to={`/onboarding/${uniqueId}`} replace />;
  }

  return <Outlet />;
};

export default PurlAuthHandler;
